import * as React from 'react';
import { useState, useEffect, useRef } from 'react';
import {
  Loader2,
  Plus,
  ImageIcon,
  ClipboardList,
  RefreshCcw,
  X,
} from 'lucide-react';
import { Storage } from 'aws-amplify';
import useOrgData from '../../../application/hooks/useOrgData';
import { PageHeader } from '../../components/page-header';
import { Button } from '../../components/ui/button';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../components/ui/toast';

declare const global: {
  me: {
    id: string;
    imageName?: string;
    name: string;
  };
};

const CDN_BASE = 'https://d22xeeaijfewl2.cloudfront.net/public/';

function getImageUrl(key: string): string {
  return `${CDN_BASE}${encodeURIComponent(key)}`;
}

export function DamageReportConfigPage() {
  const { orgData, updateOrgData } = useOrgData();
  const toast = useToast();
  const [enabled, setEnabled] = useState(false);
  const [logo, setLogo] = useState('');
  const [heroImages, setHeroImages] = useState<string[]>([]);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadingHero, setUploadingHero] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);

  const logoInputRef = useRef<HTMLInputElement>(null);
  const heroInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (orgData) {
      const dr = orgData?.configParms?.damageReports || {};
      setEnabled(dr.enabled || false);
      setLogo(dr.logo || '');
      setHeroImages(dr.heroImages || []);
    }
  }, [orgData]);

  useEffect(() => {
    if (!orgData) {
      return;
    }
    const original = orgData?.configParms?.damageReports || {};
    const current = { enabled, logo, heroImages };
    const originalNormalized = {
      enabled: original.enabled || false,
      logo: original.logo || '',
      heroImages: original.heroImages || [],
    };
    setHasChanged(
      JSON.stringify(current) !== JSON.stringify(originalNormalized)
    );
  }, [orgData, enabled, logo, heroImages]);

  const handleReset = () => {
    const dr = orgData?.configParms?.damageReports || {};
    setEnabled(dr.enabled || false);
    setLogo(dr.logo || '');
    setHeroImages(dr.heroImages || []);
  };

  const handleLogoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      setUploadingLogo(true);
      const fileName = `damage-reports/logo/${orgData?.id}/${Date.now()}_${
        file.name
      }`;
      await Storage.put(fileName, file, { contentType: file.type });
      setLogo(fileName);
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast.error('Error uploading logo');
    } finally {
      setUploadingLogo(false);
    }
    e.target.value = '';
  };

  const handleHeroSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    try {
      setUploadingHero(true);
      const fileName = `damage-reports/hero-images/${
        orgData?.id
      }/${Date.now()}_${file.name}`;
      await Storage.put(fileName, file, { contentType: file.type });
      setHeroImages((prev) => [...prev, fileName]);
    } catch (error) {
      console.error('Error uploading hero image:', error);
      toast.error('Error uploading hero image');
    } finally {
      setUploadingHero(false);
    }
    e.target.value = '';
  };

  const removeHeroImage = (index: number) => {
    setHeroImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const newConfigParms = {
        ...orgData?.configParms,
        damageReports: { enabled, logo, heroImages },
      };
      const newOrgInput = {
        ...orgData,
        configParms: JSON.stringify(newConfigParms),
      };
      await updateOrgData(newOrgInput);
      toast.success('Damage report settings updated successfully');
    } catch (error) {
      console.error('Error saving damage report settings:', error);
      toast.error('Error saving damage report settings');
    } finally {
      setIsSaving(false);
    }
  };

  if (!orgData) {
    return (
      <div className="flex flex-col h-full">
        <PageHeader
          title="Damage Reports"
          avatar={{ src: global.me?.imageName, name: global.me?.name }}
        />
        <div className="flex items-center justify-center flex-1">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Damage Reports"
        avatar={{ src: global.me?.imageName, name: global.me?.name }}
      />

      <div className="flex-1 overflow-y-auto px-4 md:px-10 py-4">
        <div className="max-w-[976px] mx-auto space-y-10">
          {/* Header Section */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4 md:gap-6">
              <div className="flex items-center justify-center size-[60px] md:size-[100px] rounded-full bg-[hsl(var(--secondary)/0.2)]">
                <ClipboardList className="size-6 md:size-8 text-[hsl(var(--secondary))]" />
              </div>
              <div>
                <h2 className="text-lg md:text-xl font-bold text-foreground tracking-tight">
                  Damage Report
                </h2>
                <p className="text-sm text-muted-foreground">
                  Configure damage report branding
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2.5">
              <span className="text-base font-semibold text-foreground">
                {enabled ? 'Enabled' : 'Disabled'}
              </span>
              <Switch checked={enabled} onCheckedChange={setEnabled} />
            </div>
          </div>

          {/* Logo + Hero Images Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Logo Card */}
            <div className="border border-border rounded-xl p-6">
              <div className="flex flex-col gap-6">
                <h4 className="text-base font-bold text-foreground">Logo</h4>
                <div
                  onClick={() => !logo && logoInputRef.current?.click()}
                  className={`bg-muted border border-dashed border-border rounded-[20px] h-[240px] md:h-[317px] flex flex-col items-center justify-center p-6 overflow-hidden ${
                    !logo ? 'cursor-pointer hover:bg-muted/80' : ''
                  } transition-colors`}
                >
                  {uploadingLogo ? (
                    <Loader2 className="size-6 animate-spin text-muted-foreground" />
                  ) : logo ? (
                    <img
                      src={getImageUrl(logo)}
                      alt="Logo"
                      className="max-h-[270px] max-w-full object-contain rounded-xl"
                    />
                  ) : (
                    <>
                      <div className="flex flex-col items-center gap-2.5">
                        <ImageIcon className="size-6 text-foreground" />
                        <p className="text-base font-semibold text-foreground">
                          No logo selected
                        </p>
                      </div>
                      <div className="flex items-center gap-2.5 h-12 px-4 py-2 bg-background border border-border rounded-lg mt-[30px]">
                        <Plus className="size-6 text-foreground" />
                        <span className="text-base font-semibold text-foreground">
                          Add logo
                        </span>
                      </div>
                    </>
                  )}
                </div>
                {logo && (
                  <button
                    onClick={() => logoInputRef.current?.click()}
                    className="flex items-center gap-2.5 h-12 px-4 py-2 bg-background border border-border rounded-lg self-end"
                  >
                    <RefreshCcw className="size-5 text-foreground" />
                    <span className="text-base font-semibold text-foreground">
                      Replace Logo
                    </span>
                  </button>
                )}
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif"
                  className="hidden"
                  onChange={handleLogoSelect}
                />
              </div>
            </div>

            {/* Hero Images Card */}
            <div className="border border-border rounded-xl p-6">
              <div className="flex flex-col gap-6">
                <h4 className="text-base font-bold text-foreground">
                  Hero images
                </h4>
                <div
                  className={`bg-muted border border-dashed border-border rounded-[20px] h-[240px] md:h-[317px] flex flex-col items-center p-6 overflow-hidden ${
                    heroImages.length === 0
                      ? 'justify-center cursor-pointer hover:bg-muted/80'
                      : 'justify-start'
                  } transition-colors`}
                  onClick={() =>
                    heroImages.length === 0 && heroInputRef.current?.click()
                  }
                >
                  {uploadingHero && heroImages.length === 0 ? (
                    <Loader2 className="size-6 animate-spin text-muted-foreground" />
                  ) : heroImages.length > 0 ? (
                    <div className="flex flex-wrap gap-4 w-full content-start">
                      {heroImages.map((image, index) => (
                        <div
                          key={index}
                          className="relative group w-[120px] h-[75px] md:w-[162px] md:h-[101px]"
                        >
                          <img
                            src={getImageUrl(image)}
                            alt={`Hero ${index + 1}`}
                            className="w-full h-full object-cover rounded-lg border border-border"
                          />
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              removeHeroImage(index);
                            }}
                            className="absolute top-2 right-2 size-8 rounded-full bg-background border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="size-4 text-foreground" />
                          </button>
                        </div>
                      ))}
                      {uploadingHero && (
                        <div className="w-[120px] h-[75px] md:w-[162px] md:h-[101px] rounded-lg border border-dashed border-border flex items-center justify-center bg-background">
                          <Loader2 className="size-5 animate-spin text-muted-foreground" />
                        </div>
                      )}
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col items-center gap-2.5">
                        <ImageIcon className="size-6 text-foreground" />
                        <p className="text-base font-semibold text-foreground">
                          No hero uploaded
                        </p>
                      </div>
                      <div className="flex items-center gap-2.5 h-12 px-4 py-2 bg-background border border-border rounded-lg mt-[30px]">
                        <Plus className="size-6 text-foreground" />
                        <span className="text-base font-semibold text-foreground">
                          Add hero image
                        </span>
                      </div>
                    </>
                  )}
                </div>
                {heroImages.length > 0 && (
                  <button
                    onClick={() => heroInputRef.current?.click()}
                    className="flex items-center gap-2.5 h-12 px-4 py-2 bg-background border border-border rounded-lg self-end"
                  >
                    <Plus className="size-5 text-foreground" />
                    <span className="text-base font-semibold text-foreground">
                      Add more
                    </span>
                  </button>
                )}
                <input
                  ref={heroInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/gif"
                  className="hidden"
                  onChange={handleHeroSelect}
                />
              </div>
            </div>
          </div>

          {/* Footer Buttons */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pb-8">
            <Button
              variant="outline"
              onClick={handleReset}
              disabled={!hasChanged || isSaving}
              className="py-3 px-6 text-base font-bold"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!hasChanged || isSaving}
              className="py-3 px-6 text-base font-bold"
            >
              {isSaving ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                'Save Changes'
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
